import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PAYMENT_CONFIG } from "@/constants/status";
import { taxInvoices } from "@/mocks/invoices";
import type { PaymentMethod, TaxInvoice } from "@/types";
import { formatSAR } from "@/utils/format";
import { downloadCsv } from "@/utils/csv";
import InvoicesTable from "./components/InvoicesTable";
import TaxInvoiceView from "./components/TaxInvoiceView";

const PAYMENT_FILTERS: { key: PaymentMethod | "all"; label: string }[] = [
  { key: "all", label: "كل الطرق" },
  { key: "cash", label: "كاش" },
  { key: "card", label: "بطاقة" },
  { key: "applepay", label: "Apple Pay" },
  { key: "wallet", label: "محفظة" },
];

export default function InvoicesPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [payment, setPayment] = useState<PaymentMethod | "all">("all");
  const [exported, setExported] = useState<number | null>(null);
  const [printInvoice, setPrintInvoice] = useState<TaxInvoice | null>(null);

  const list = taxInvoices;

  useEffect(() => {
    const handleAfterPrint = () => setPrintInvoice(null);
    window.addEventListener("afterprint", handleAfterPrint);
    return () => window.removeEventListener("afterprint", handleAfterPrint);
  }, []);

  const summary = useMemo(() => {
    const totalSales = list.reduce((sum, invoice) => sum + invoice.total, 0);
    const totalVat = list.reduce((sum, invoice) => sum + invoice.vat, 0);
    return { count: list.length, totalSales, totalVat };
  }, [list]);

  const branchSummary = useMemo(() => {
    const map = new Map<
      string,
      { branch: string; count: number; total: number; vat: number }
    >();
    list.forEach((invoice) => {
      const existing = map.get(invoice.branch);
      if (existing) {
        existing.count += 1;
        existing.total += invoice.total;
        existing.vat += invoice.vat;
      } else {
        map.set(invoice.branch, {
          branch: invoice.branch,
          count: 1,
          total: invoice.total,
          vat: invoice.vat,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [list]);

  const visible = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    return list.filter((invoice) => {
      const matchPayment = payment === "all" || invoice.payment === payment;
      const matchQuery =
        keyword === "" ||
        invoice.id.toLowerCase().includes(keyword) ||
        invoice.customer.toLowerCase().includes(keyword);
      return matchPayment && matchQuery;
    });
  }, [list, payment, query]);

  const handleExport = () => {
    const headers = [
      "رقم الفاتورة",
      "التاريخ",
      "الفرع",
      "العميل",
      "الجوال",
      "طريقة الدفع",
      "النوع",
      "إجمالي المنتجات شامل الضريبة",
      "الخصم",
      "الضريبة (15%)",
      "الإجمالي",
    ];
    const rows = visible.map((invoice) => [
      invoice.id,
      invoice.date,
      invoice.branch,
      invoice.customer,
      invoice.phone,
      PAYMENT_CONFIG[invoice.payment].label,
      invoice.invoiceType === "standard" ? "ضريبية" : "ضريبية مبسطة",
      invoice.subtotal.toFixed(2),
      invoice.discount.toFixed(2),
      invoice.vat.toFixed(2),
      invoice.total.toFixed(2),
    ]);
    downloadCsv("tax-invoices.csv", headers, rows);
    setExported(rows.length);
    window.setTimeout(() => setExported(null), 2500);
  };

  const handlePrint = (invoice: TaxInvoice) => {
    setPrintInvoice(invoice);
    window.setTimeout(() => {
      window.print();
    }, 120);
  };

  return (
    <div className="space-y-4">
      <div className="yc-screen-only space-y-4">
        <section className="yc-fade-up rounded-card border border-primary-200 bg-primary-50 p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-white text-xl">
                <i className="ri-shield-check-line"></i>
              </span>
              <div>
                <h2 className="text-base font-bold text-foreground-950">
                  فواتير متوافقة مع ZATCA
                </h2>
                <p className="mt-1 text-sm leading-6 text-foreground-600">
                  كل فاتورة تحتوي رمز QR مشفّر (Base64 / TLV) وفق متطلبات هيئة
                  الزكاة والضريبة والجمارك.
                </p>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 self-start rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-primary-700">
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              المرحلة الثانية مُفعّلة
            </span>
          </div>
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
            <div className="flex items-center gap-2 text-foreground-500">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-file-list-3-line"></i>
              </span>
              <span className="text-sm">عدد الفواتير</span>
            </div>
            <p className="mt-3 text-3xl font-bold text-foreground-950">
              {summary.count}
            </p>
          </div>
          <div className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
            <div className="flex items-center gap-2 text-foreground-500">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 text-accent-800">
                <i className="ri-money-dollar-circle-line"></i>
              </span>
              <span className="text-sm">إجمالي المبيعات</span>
            </div>
            <p className="mt-3 text-3xl font-bold text-foreground-950">
              {formatSAR(summary.totalSales)}
            </p>
          </div>
          <div className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
            <div className="flex items-center gap-2 text-foreground-500">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-800">
                <i className="ri-percent-line"></i>
              </span>
              <span className="text-sm">ضريبة القيمة المضافة</span>
            </div>
            <p className="mt-3 text-3xl font-bold text-foreground-950">
              {formatSAR(summary.totalVat)}
            </p>
          </div>
        </div>

        <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-base font-bold text-foreground-950">
              المبيعات حسب الفرع
            </h2>
            <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
              {branchSummary.length} فرع
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {branchSummary.map((item) => {
              const maxTotal = branchSummary[0]?.total ?? 1;
              const share = Math.round((item.total / maxTotal) * 100);
              return (
                <div
                  key={item.branch}
                  className="yc-card-hover rounded-box border border-background-200 bg-background-50 p-5"
                >
                  <div className="flex items-center gap-3">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                      <i className="ri-store-2-line"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-foreground-950 truncate">
                        {item.branch}
                      </p>
                      <p className="text-xs text-foreground-500 mt-0.5">
                        {item.count} فاتورة
                      </p>
                    </div>
                  </div>
                  <p className="mt-4 text-2xl font-bold text-foreground-950">
                    {formatSAR(item.total)}
                  </p>
                  <p className="mt-1 text-xs text-foreground-500">
                    الضريبة {formatSAR(item.vat)}
                  </p>
                  <div className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                    <div
                      className="h-full rounded-full bg-primary-500"
                      style={{ width: `${share}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-2">
              {PAYMENT_FILTERS.map((item) => {
                const active = payment === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setPayment(item.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                      active
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center rounded-btn border border-background-200 bg-white px-3 focus-within:ring-2 focus-within:ring-primary-300">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line"></i>
              </span>
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="ابحث برقم الفاتورة أو العميل"
                className="w-full sm:w-64 h-11 bg-transparent px-2 text-sm outline-none placeholder:text-foreground-300"
              />
            </div>
          </div>
        </section>

        <section className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card overflow-hidden">
          <div className="px-5 py-4 border-b border-background-100 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-bold text-foreground-950">
              سجل الفواتير الضريبية
            </h2>
            <div className="flex items-center gap-3">
              <span className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600">
                {visible.length} فاتورة
              </span>
              <button
                type="button"
                onClick={handleExport}
                disabled={visible.length === 0}
                className="inline-flex items-center gap-2 h-9 rounded-full bg-primary-500 px-4 text-xs font-semibold text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-download-2-line"></i>
                تصدير CSV
              </button>
              {exported !== null && (
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700">
                  <i className="ri-checkbox-circle-line"></i>
                  تم تصدير {exported} فاتورة
                </span>
              )}
            </div>
          </div>
          <InvoicesTable
            invoices={visible}
            onSelect={(invoice) => navigate(`/invoices/${invoice.id}`)}
            onPrint={handlePrint}
          />
        </section>
      </div>

      {printInvoice && (
        <div className="yc-print-only">
          <TaxInvoiceView invoice={printInvoice} />
        </div>
      )}
    </div>
  );
}